import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2, Phone, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  hotel: 'Hotel booking',
  flight: 'Flight booking',
  train: 'Train / road travel',
  bus: 'Bus booking',
};

export default function BookRequestPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const itemType = params.get('type') || 'item';
  const itemLabel = params.get('item') || '';
  const itinRef = params.get('ref') || '';

  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setError('Please share your name and phone number so we can reach you.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/book-request`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        item_type: itemType,
        item_label: itemLabel,
        itinerary_ref: itinRef,
        notes: form.notes.trim(),
      });
      setDone(true);
    } catch (err) {
      setError('Could not send your request. Please call us on +91 63588 46179 and we will sort it out.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-16">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-stone-200 p-8 text-center" data-testid="book-request-success">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <Check className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">Request received</h1>
          <p className="text-stone-600 mb-6">
            Our team will contact you shortly with live availability and our best price for{' '}
            <span className="font-semibold text-stone-800">{itemLabel || 'your request'}</span>.
          </p>
          <a
            href="tel:+916358846179"
            className="inline-flex items-center gap-2 text-amber-700 font-semibold hover:text-amber-800 transition-colors"
            data-testid="book-request-call-link"
          >
            <Phone className="w-4 h-4" /> Need it urgently? Call +91 63588 46179
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-12">
      <div className="max-w-lg mx-auto">
        <button
          onClick={() => navigate('/instant-itinerary')}
          className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 mb-5 transition-colors"
          data-testid="book-request-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden" data-testid="book-request-page">
          <div className="bg-stone-900 px-7 py-5">
            <p className="text-[11px] uppercase tracking-widest text-amber-400 font-bold mb-1">
              {TYPE_LABELS[itemType] || 'Booking request'}
            </p>
            <h1 className="text-xl font-bold text-white">Book this with us</h1>
          </div>

          {itemLabel && (
            <div className="px-7 py-4 bg-amber-50 border-b border-amber-100">
              <p className="text-xs uppercase tracking-wide text-amber-800 font-bold mb-1">You selected</p>
              <p className="text-sm text-stone-800 font-medium" data-testid="book-request-item">{itemLabel}</p>
            </div>
          )}

          <form onSubmit={submit} className="px-7 py-6 space-y-4">
            <p className="text-sm text-stone-600">
              Share your details and our team will confirm live availability and send you our best price — often better
              than public rates.
            </p>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Your name *</label>
              <input
                value={form.name}
                onChange={update('name')}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-shadow"
                placeholder="Full name"
                data-testid="book-request-name"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Phone / WhatsApp *</label>
              <input
                value={form.phone}
                onChange={update('phone')}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-shadow"
                placeholder="+91 ..."
                data-testid="book-request-phone"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Email (optional)</label>
              <input
                type="email"
                value={form.email}
                onChange={update('email')}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-shadow"
                placeholder="you@example.com"
                data-testid="book-request-email"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Anything specific?</label>
              <textarea
                value={form.notes}
                onChange={update('notes')}
                rows={3}
                className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none transition-shadow resize-none"
                placeholder="Dates, room type, number of travellers, berth preference..."
                data-testid="book-request-notes"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="book-request-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="book-request-submit"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>) : 'Send booking request'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
